// In-memory queues survive route changes. They are scoped to a user, school and
// list; no learner data is written to browser storage.
const queues = new Map();
const cleanState = Object.freeze({ isSaving: false, saveError: null, hasUnsavedChanges: false });

export function createSmartListSaveQueue() {
  let pending = null;
  let draft = null;
  let inFlight = null;
  let timer = null;
  let attempt = 0;
  let write;
  let canSave = () => true;
  let state = cleanState;
  const listeners = new Set();
  const publish = changes => {
    state = { ...state, ...changes };
    listeners.forEach(listener => listener());
  };
  const schedule = delay => {
    clearTimeout(timer);
    timer = setTimeout(() => { void flush(); }, delay);
  };
  const flush = () => {
    clearTimeout(timer);
    timer = null;
    if (inFlight) return inFlight;
    if (!pending) return Promise.resolve(true);
    if (!canSave()) {
      publish({ saveError: 'Sign in again to finish saving your changes.', hasUnsavedChanges: true });
      return Promise.resolve(false);
    }
    publish({ isSaving: true, saveError: null });
    inFlight = (async () => {
      while (pending) {
        if (!canSave()) {
          publish({ saveError: 'Sign in again to finish saving your changes.' });
          return false;
        }
        const fields = pending;
        pending = null;
        try {
          await write({ ...fields, lastUsed: new Date() });
          attempt = 0;
        } catch {
          pending = { ...fields, ...pending };
          publish({ saveError: 'Waiting for your changes to save. This will continue automatically.' });
          schedule(Math.min(2000 * 2 ** attempt++, 30000));
          return false;
        }
      }
      draft = null;
      return true;
    })().finally(() => {
      inFlight = null;
      publish({ isSaving: false, hasUnsavedChanges: !!pending });
    });
    return inFlight;
  };
  return {
    configure(nextWrite, nextCanSave = () => true) { write = nextWrite; canSave = nextCanSave; },
    enqueue(fields, delay = 500) {
      pending = { ...pending, ...fields };
      draft = { ...draft, ...fields };
      publish({ hasUnsavedChanges: true });
      schedule(delay);
    },
    flush,
    resume() { if (pending && !timer && !inFlight) void flush(); },
    getSnapshot: () => state,
    getDraft: () => draft,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    dispose() { clearTimeout(timer); listeners.clear(); },
  };
}

export function getSmartListSaveQueue(scope, listId) {
  if (!queues.has(scope)) queues.set(scope, new Map());
  const scopedQueues = queues.get(scope);
  const key = String(listId);
  if (!scopedQueues.has(key)) scopedQueues.set(key, createSmartListSaveQueue());
  return scopedQueues.get(key);
}

const allQueues = () => [...queues.values()].flatMap(scoped => [...scoped.values()]);

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', event => {
    if (allQueues().some(queue => queue.getSnapshot().hasUnsavedChanges)) {
      event.preventDefault();
      event.returnValue = '';
    }
  });
  window.addEventListener('online', () => {
    for (const queue of allQueues()) {
      if (queue.getSnapshot().hasUnsavedChanges) void queue.flush();
    }
  });
}
